import React, { type MouseEventHandler, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useMedia } from 'react-use';

import { Button, Text, UI_KIT_TEXT_TYPOGRAPHY } from 'platform-components';
import { LessonCalendar, List } from 'education-icons';

import { AdaptiveTabs } from 'common.components/adaptive-tabs/adaptive-tabs';
import { getCurrentYearCourseId } from 'common.components/course/selectors';
import { getCurrentClass } from 'common.components/user/selectors';
import { getLessonsCounts, getSubjects } from 'store/courses/selectors';
import type { LessonsCount } from 'store/courses/typings';
import { getTrimmedName } from 'utils/get-trimmed-name';

import { getFormattedLessonsCount } from '../../bundles/lab/components/course-view/utils';
import { COURSE_VIEW_LESSONS_TABS } from '../../bundles/lab/components/course-view/typings';
import { CourseViewLessonsTabs } from '../../bundles/lab/components/course-view/__lessons-tabs/course-view__lessons-tabs';
import AddLessonButton from '../../bundles/lab/components/course-view/__add-lesson-button/course-view__add-lesson-button';
import { CourseCalendar } from '../../bundles/lab/components/course-calendar/course-calendar';
import Drafts from '../../bundles/lab/components/course-view/__drafts/course-view__drafts';
import CompletedLessons from '../../bundles/lab/components/course-view/__completed-lessons/course-view__completed-lessons';
import ActiveLessonsTab from '../../bundles/lab/components/course-view/__active-tab/course-view__active-tab';

import './course-calendar-widget.scss';

type CourseCalendarViewType = 'calendar' | 'lessons';

interface CourseCalendarWidgetProps {
    mediaMobile?: string;
    mediaDesktop?: string;
}

// eslint-disable-next-line complexity
export const CourseCalendarWidget = ({
    mediaMobile = '(max-width: 600px)',
    mediaDesktop = '(min-width: 1280px)',
}: CourseCalendarWidgetProps) => {
    const isMobile = useMedia(mediaMobile);
    const isDesktop = useMedia(mediaDesktop);

    const { classId } = useParams<Record<string, string>>();

    const allCoursesSubjects = useSelector((storeState) => getSubjects(storeState, { classId }));

    const [selectedSubject, setSelectedSubject] = useState<string>(allCoursesSubjects[0]?.subject);
    const [lessonTab, setLessonTab] = useState<COURSE_VIEW_LESSONS_TABS>(COURSE_VIEW_LESSONS_TABS.ACTIVE);
    const [courseCalendarView, setCourseCalendarView] = useState<CourseCalendarViewType>('calendar');

    const team = useSelector((storeState) => getCurrentClass(storeState, { classId }));
    const courseId = useSelector((storeState) =>
        getCurrentYearCourseId(storeState, { classId, subject: selectedSubject })
    );

    const lessonsCount = useSelector((storeState) => getLessonsCounts(storeState, { courseId }));
    const formattedLessonsCount = getFormattedLessonsCount(lessonsCount as LessonsCount);

    const handleTabClick = useCallback((tab: COURSE_VIEW_LESSONS_TABS) => {
        setLessonTab(tab);
    }, []);
    const handleSelectTab = useCallback((subject: string) => {
        setSelectedSubject(subject);
    }, []);

    const showCalendar = useCallback<MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>>(() => {
        setCourseCalendarView('calendar');
    }, []);
    const showLessons = useCallback<MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>>(() => {
        setCourseCalendarView('lessons');
    }, []);

    const tabs = useMemo(() => {
        return allCoursesSubjects.map((subject) => ({
            value: subject.subject,
            content: subject.name,
        }));
    }, [allCoursesSubjects]);

    const calendarControls = (
        <div className="course-calendar-widget__controls">
            <AddLessonButton
                classId={classId}
                subject={selectedSubject}
                textId={isMobile || isDesktop ? undefined : 'courseView.createLessonButton.short'}
            />
            <Button
                icon={courseCalendarView === 'calendar' ? <List /> : <LessonCalendar />}
                onClick={courseCalendarView === 'calendar' ? showLessons : showCalendar}
                size={isMobile ? Button.SIZE.L : undefined}
                view={Button.VIEW.PSEUDO}
            />
        </div>
    );

    if (!selectedSubject) {
        return null;
    }

    return (
        <div className="course-calendar-widget">
            <Text
                id="courseView.headerWithClassName"
                typography={UI_KIT_TEXT_TYPOGRAPHY.headlineLMedium}
                values={{ name: getTrimmedName(team?.name), level: team?.level }}
            />
            <AdaptiveTabs mediaMobile={mediaMobile} onSelect={handleSelectTab} tabs={tabs} />
            {courseCalendarView === 'calendar' && (
                <CourseCalendar
                    additionalControls={calendarControls}
                    classId={classId}
                    editorId={selectedSubject}
                    key={classId}
                    subject={selectedSubject}
                />
            )}
            {courseCalendarView === 'lessons' && (
                <div>
                    <div className="course-calendar-widget__lesson-tabs-menu">
                        <CourseViewLessonsTabs
                            currentTab={lessonTab}
                            disableRouter={true}
                            isClosed={false}
                            lessonsCounters={formattedLessonsCount}
                            onTabClick={handleTabClick}
                        />
                        {calendarControls}
                    </div>
                    {lessonTab === COURSE_VIEW_LESSONS_TABS.DRAFTS && (
                        <Drafts classId={classId} key={selectedSubject} subject={selectedSubject} />
                    )}
                    {lessonTab === COURSE_VIEW_LESSONS_TABS.ACTIVE && (
                        <ActiveLessonsTab classId={classId} key={selectedSubject} subject={selectedSubject} />
                    )}
                    {lessonTab === COURSE_VIEW_LESSONS_TABS.COMPLETE && (
                        <CompletedLessons classId={classId} key={selectedSubject} subject={selectedSubject} />
                    )}
                </div>
            )}
        </div>
    );
};
